<script lang="ts">
  /**
   * Le Fil — lire une conversation et répondre.
   *
   * Plus de bulles de messagerie : des cartes empilées sur toute la largeur,
   * avec un liseré gauche pour dire qui parle. Sur un écran de 375px,
   * l'alignement gauche/droite d'une messagerie coûtait la moitié de la
   * largeur de lecture pour une information que le liseré donne gratuitement.
   *
   * L'écran porte son propre en-tête (retour + actions) : la topbar globale
   * s'efface ici, sinon deux barres se superposeraient.
   */

  import { gmailState } from "../gmail/store.svelte";
  import { authState } from "../auth/store.svelte";
  import { loadThread, markUnread, archiveThread } from "../gmail/sync";
  import { getHeader, parseFrom } from "../gmail/headers";
  import type { GmailMessage } from "../gmail/store.svelte";
  import MessageCard from "./MessageCard.svelte";
  import ReplyComposer from "./ReplyComposer.svelte";
  import Icon from "./shared/Icon.svelte";
  import { openSnooze } from "./snoozeStore.svelte";
  import { popNav } from "../gestures/historyNav";
  import { haptic } from "../gestures/haptic";

  $effect(() => {
    const id = gmailState.selectedThreadId;
    if (id) void loadThread(id);
  });

  // === L'interlocuteur =====================================================
  interface Counterparty {
    name: string;
    email: string;
    domain: string;
  }

  function buildCounterparty(messages: GmailMessage[], myEmail: string): Counterparty {
    const me = myEmail.toLowerCase();
    let name = "";
    let email = "";
    for (const m of messages) {
      const parsed = parseFrom(getHeader(m, "From"));
      if (parsed.email && parsed.email.toLowerCase() !== me) {
        name = parsed.name;
        email = parsed.email;
        break;
      }
    }
    if (!email && messages.length > 0) {
      const parsed = parseFrom(getHeader(messages[0], "To"));
      name = parsed.name;
      email = parsed.email;
    }
    return { name, email, domain: (email.split("@")[1] ?? "").toLowerCase() };
  }

  const counterparty = $derived.by<Counterparty>(() => {
    const t = gmailState.currentThread;
    if (!t || t.messages.length === 0) return { name: "", email: "", domain: "" };
    return buildCounterparty(t.messages, authState.email ?? "");
  });

  const displayName = $derived(
    counterparty.name || counterparty.email || "Inconnu",
  );
  const firstName = $derived(displayName.split(/\s+/)[0]);

  const subject = $derived.by(() => {
    const t = gmailState.currentThread;
    if (!t || t.messages.length === 0) return "";
    return getHeader(t.messages[0], "Subject") ?? "(sans objet)";
  });

  const initials = $derived.by(() => {
    const src = counterparty.name?.trim() || counterparty.email || "?";
    const parts = src.split(/[\s.@]+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  });

  // === Actions d'en-tête ===================================================
  function back() {
    popNav();
  }

  function onSnooze() {
    const id = gmailState.selectedThreadId;
    if (id) openSnooze(id);
  }

  function onUnread() {
    const id = gmailState.selectedThreadId;
    if (!id) return;
    haptic("medium");
    void markUnread(id);
    popNav();
  }

  function onArchive() {
    const id = gmailState.selectedThreadId;
    if (!id) return;
    haptic("medium");
    void archiveThread(id);
    popNav();
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") back();
  }
</script>

<svelte:window onkeydown={onKeydown} />

<div class="thread">
  <header class="head">
    <div class="row1">
      <button class="icon-btn" onclick={back} aria-label="Retour">
        <Icon name="chevron-left" size={20} />
      </button>
      <div class="avatar" aria-hidden="true">{initials}</div>
      <div class="who">
        <div class="name">{displayName}</div>
        {#if counterparty.domain}
          <div class="domain">{counterparty.domain}</div>
        {/if}
      </div>
      <button class="icon-btn" onclick={onSnooze} aria-label="Reporter" title="Reporter">
        <Icon name="clock" size={17} />
      </button>
      <button class="icon-btn" onclick={onUnread} aria-label="Marquer non lu" title="Marquer non lu">
        <Icon name="mail" size={17} />
      </button>
      <button class="icon-btn" onclick={onArchive} aria-label="Archiver" title="Archiver">
        <Icon name="archive" size={17} />
      </button>
    </div>
    {#if subject}
      <div class="subject">{subject}</div>
    {/if}
  </header>

  <div class="body">
    {#if gmailState.threadError}
      <p class="err">{gmailState.threadError}</p>
    {:else if gmailState.loadingThread && !gmailState.currentThread}
      <div class="skels">
        {#each Array(3) as _, i (i)}
          <div class="skel-card">
            <span class="mfx-skel w60"></span>
            <span class="mfx-skel"></span>
            <span class="mfx-skel w80"></span>
          </div>
        {/each}
      </div>
    {:else if gmailState.currentThread}
      {@const msgs = gmailState.currentThread.messages}
      {#each msgs as msg, i (msg.id)}
        <MessageCard
          message={msg}
          isLatest={i === msgs.length - 1}
          counterpartyFirstName={firstName}
        />
      {/each}
    {/if}
  </div>

  <ReplyComposer recipient={firstName} />
</div>

<style>
  .thread {
    min-height: 100%;
    background: var(--canvas);
  }

  /* === En-tête ============================================================ */
  .head {
    position: sticky;
    top: 0;
    z-index: 20;
    background: color-mix(in srgb, var(--bg) 86%, transparent);
    backdrop-filter: saturate(180%) blur(20px);
    -webkit-backdrop-filter: saturate(180%) blur(20px);
    border-bottom: 1px solid var(--border-subtle);
  }
  .row1 {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: calc(env(safe-area-inset-top) + 9px) 10px 9px 6px;
  }
  .icon-btn {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    border-radius: var(--radius-md);
    color: var(--fg-muted);
    transition: var(--tr);
  }
  .icon-btn:active { background: var(--bg-hover); }
  .icon-btn:first-child { width: 36px; height: 36px; color: var(--fg); }

  .avatar {
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: var(--accent-soft);
    color: var(--accent);
    font-size: 11.5px;
    font-weight: 600;
  }
  .who {
    flex: 1;
    min-width: 0;
  }
  .name {
    font-size: 13px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .domain {
    font-family: var(--font-mono);
    font-size: 9px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--fg-subtle);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .subject {
    padding: 0 16px 11px;
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    font-size: 19px;
    line-height: 1.24;
    text-wrap: pretty;
  }

  /* === Corps ============================================================== */
  /* La marge basse laisse passer la barre de réponse ET la tab bar. */
  .body { padding: 12px 12px 150px; }

  .err {
    margin: 0;
    padding: 10px 12px;
    border-radius: var(--radius-lg);
    background: var(--bad-bg);
    color: var(--bad);
    font-size: 12.5px;
  }

  .skels { display: flex; flex-direction: column; gap: 14px; }
  .skel-card {
    display: flex;
    flex-direction: column;
    gap: 7px;
    padding: 13px 14px;
    background: var(--bg);
    border: 1px solid var(--border-subtle);
    border-radius: 11px;
  }
  .w60 { width: 60%; }
  .w80 { width: 80%; }
</style>
